/**
 * CVR v6 Radar Chart — 8-component CVR visualization with radar chart + breakdown bars
 * Components: Production (25%), Approach (12%), Power/Stuff (10%), Speed/Defense (8%),
 *             Situational (10%), Durability (10%), Contract (10%), Trajectory (15%)
 */
import React from 'react';
import { Box, Typography, Chip, useTheme, alpha, Tooltip, Grid } from '@mui/material';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer } from 'recharts';
import { motion } from 'framer-motion';

const CVR_COMPONENTS = [
  { key: 'production', label: 'Production', maxWeight: 25, icon: '⚡', description: 'Park-adjusted wRC+/OPS+ or ERA+/FIP- blend' },
  { key: 'approach', label: 'Approach', maxWeight: 12, icon: '🎯', description: 'Plate discipline, BB/K ratio, BABIP sustainability' },
  { key: 'powerStuff', label: 'Power/Stuff', maxWeight: 10, icon: '💥', description: 'ISO, HR rate (hitters) or K%, K-BB% (pitchers)' },
  { key: 'speedDefense', label: 'Speed/Def', maxWeight: 8, icon: '🛡️', description: 'Baserunning value + positional defensive contribution' },
  { key: 'situational', label: 'Situational', maxWeight: 10, icon: '🔄', description: 'Platoon balance, home/away, matchup breadth' },
  { key: 'durability', label: 'Durability', maxWeight: 10, icon: '🏋️', description: 'Games/IP volume, consistency, workload' },
  { key: 'contract', label: 'Contract', maxWeight: 10, icon: '💰', description: 'Surplus value, salary efficiency' },
  { key: 'trajectory', label: 'Trajectory', maxWeight: 15, icon: '📈', description: 'Age curve, future projection, trend momentum' },
];

const GRADE_CONFIG = {
  'A+': { color: '#00e676', bg: '#00e67618' },
  'A':  { color: '#4caf50', bg: '#4caf5018' },
  'A-': { color: '#66bb6a', bg: '#66bb6a18' },
  'B+': { color: '#8bc34a', bg: '#8bc34a18' },
  'B':  { color: '#cddc39', bg: '#cddc3918' },
  'B-': { color: '#fdd835', bg: '#fdd83518' },
  'C+': { color: '#ffc107', bg: '#ffc10718' },
  'C':  { color: '#ff9800', bg: '#ff980018' },
  'C-': { color: '#ff7043', bg: '#ff704318' },
  'D+': { color: '#f44336', bg: '#f4433618' },
  'D':  { color: '#ef5350', bg: '#ef535018' },
  'F':  { color: '#c62828', bg: '#c6282818' },
};

const getGradeFromCVR = (cvr) => {
  if (cvr >= 90) return 'A+';
  if (cvr >= 80) return 'A';
  if (cvr >= 75) return 'A-';
  if (cvr >= 70) return 'B+';
  if (cvr >= 65) return 'B';
  if (cvr >= 55) return 'B-';
  if (cvr >= 50) return 'C+';
  if (cvr >= 45) return 'C';
  if (cvr >= 40) return 'C-';
  if (cvr >= 35) return 'D+';
  if (cvr >= 25) return 'D';
  return 'F';
};

const getTierFromCVR = (cvr) => {
  if (cvr >= 90) return 'Untouchable';
  if (cvr >= 80) return 'Franchise';
  if (cvr >= 70) return 'All-Star';
  if (cvr >= 55) return 'Above Average';
  if (cvr >= 45) return 'Average';
  if (cvr >= 35) return 'Below Average';
  if (cvr >= 25) return 'Role Player';
  return 'Replacement';
};

const CVRRadarChart = ({ cvrData, compact = false, showBreakdown = true }) => {
  const theme = useTheme();
  
  if (!cvrData) return null;
  
  const cvr = cvrData.cvr || cvrData.total || 0;
  const breakdown = cvrData.breakdown || cvrData.components || {};
  const grade = cvrData.grade || getGradeFromCVR(cvr);
  const tierRaw = cvrData.tier;
  const tier = typeof tierRaw === 'object' ? (tierRaw.label || getTierFromCVR(cvr)) : (tierRaw || getTierFromCVR(cvr));
  const gradeConfig = GRADE_CONFIG[grade] || GRADE_CONFIG['C'];
  
  // Build radar data — normalize each component to 0-100 scale based on its max weight
  const radarData = CVR_COMPONENTS.map(comp => ({
    subject: `${comp.icon} ${comp.label}`,
    value: Math.min(100, ((breakdown[comp.key] || 0) / comp.maxWeight) * 100),
    rawValue: breakdown[comp.key] || 0,
    maxWeight: comp.maxWeight,
    fullMark: 100
  }));

  return (
    <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }}>
      <Box>
        {/* CVR Score Header — centered ring + grade + tier */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: compact ? 1 : 1.5 }}>
          <Box sx={{
            width: compact ? 56 : 72,
            height: compact ? 56 : 72,
            borderRadius: '50%',
            background: `conic-gradient(${gradeConfig.color} ${cvr * 3.6}deg, ${alpha(theme.palette.divider, 0.15)} 0deg)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: `0 0 16px ${alpha(gradeConfig.color, 0.15)}`,
          }}>
            <Box sx={{
              width: compact ? 44 : 58,
              height: compact ? 44 : 58,
              borderRadius: '50%',
              bgcolor: 'background.paper',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column',
            }}>
              <Typography variant={compact ? 'h6' : 'h5'} fontWeight={800} sx={{ color: gradeConfig.color, lineHeight: 1, fontSize: compact ? '1rem' : '1.35rem' }}>
                {cvr.toFixed(0)}
              </Typography>
              <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: 0.5, color: 'text.secondary' }}>
                CVR
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ textAlign: 'left' }}>
            <Chip
              label={grade}
              size="small"
              sx={{
                bgcolor: gradeConfig.bg,
                color: gradeConfig.color,
                fontWeight: 800,
                fontSize: compact ? '0.7rem' : '0.8rem',
                mb: 0.5,
                border: `1px solid ${alpha(gradeConfig.color, 0.3)}`,
                height: 24,
              }}
            />
            <Typography variant="body2" sx={{ fontSize: compact ? '0.75rem' : '0.85rem', fontWeight: 600, color: 'text.secondary' }}>
              {tier}
            </Typography>
          </Box>
        </Box>
        
        {/* Radar Chart */}
        {!compact && (
          <Box sx={{ width: '100%', height: 270 }}>
            <ResponsiveContainer>
              <RadarChart cx="50%" cy="50%" outerRadius="65%" data={radarData}>
                <PolarGrid stroke={alpha(theme.palette.divider, 0.2)} />
                <PolarAngleAxis
                  dataKey="subject"
                  tick={{ fill: theme.palette.text.secondary, fontSize: 10, fontWeight: 600 }}
                />
                <PolarRadiusAxis
                  angle={90}
                  domain={[0, 100]}
                  tick={false}
                  axisLine={false}
                />
                <Radar
                  name="CVR"
                  dataKey="value"
                  stroke={gradeConfig.color}
                  fill={gradeConfig.color}
                  fillOpacity={0.2}
                  strokeWidth={2}
                />
              </RadarChart>
            </ResponsiveContainer>
          </Box>
        )}
        
        {/* Component Breakdown — compact 2-column grid */}
        {showBreakdown && (
          <Grid container spacing={0.5} sx={{ mt: compact ? 0.5 : 1 }}>
            {CVR_COMPONENTS.map((comp, i) => {
              const value = breakdown[comp.key] || 0;
              const pct = (value / comp.maxWeight) * 100;
              const barColor = pct >= 80 ? gradeConfig.color : pct >= 50 ? theme.palette.info.main : alpha(theme.palette.text.secondary, 0.5);
              return (
                <Grid item xs={6} key={comp.key}>
                  <Tooltip title={comp.description} placement="top" arrow>
                    <Box sx={{
                      display: 'flex', alignItems: 'center', gap: 0.5, px: 0.75, py: 0.35, borderRadius: 1,
                      '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) },
                    }}>
                      <Typography sx={{ fontSize: '0.75rem', minWidth: 16 }}>{comp.icon}</Typography>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary', display: 'block', lineHeight: 1.1, mb: 0.15 }}>
                          {comp.label}
                        </Typography>
                        <Box sx={{ height: 5, bgcolor: alpha(theme.palette.divider, 0.12), borderRadius: 1, overflow: 'hidden' }}>
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${Math.min(100, pct)}%` }}
                            transition={{ duration: 0.5, delay: i * 0.04 }}
                            style={{ height: '100%', borderRadius: 4, background: barColor }}
                          />
                        </Box>
                      </Box>
                      <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 700, color: barColor, minWidth: 34, textAlign: 'right' }}>
                        {value.toFixed(1)}/{comp.maxWeight}
                      </Typography>
                    </Box>
                  </Tooltip>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>
    </motion.div>
  );
};

export default CVRRadarChart;
export { CVR_COMPONENTS, GRADE_CONFIG, getGradeFromCVR, getTierFromCVR };
